import {
  Alert,
  Button,
  DescriptionList,
  formatDate,
  Modal,
  Money,
  SkeletonText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  TypeToConfirm,
  useToast,
  type DescriptionListItem,
} from '@hkdevs/design-system';
import { useState } from 'react';
import {
  useClearPendingPay,
  useDeleteUpload,
  useDeletionPreview,
  useUpload,
  type PendingPaySummary,
  type Upload,
} from '../../api/queries/uploads';
import { dollars } from '../lineItems/LineItemList';
import { downloadText } from '../payHistory/csv';
import { withReference } from '../../api/withReference';

/** An upload a modal is open for: its id and file name, from a list row or the drawer. */
export interface UploadRef {
  id: string;
  fileName: string;
}

/** A field, quoted when it holds a comma, quote or line break (RFC 4180). */
const field = (value: string | number) => {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

/** The report's problems as CSV: Row, Payee, Field, Value, Problem. */
export function errorReportCsv(upload: Upload) {
  const rows = [
    ['Row', 'Payee', 'Field', 'Value', 'Problem'],
    ...(upload.errorReport?.problemRows ?? []).map((p) => [
      p.row,
      p.payee,
      p.field,
      p.value,
      p.problem,
    ]),
  ];
  return `${rows.map((row) => row.map(field).join(',')).join('\r\n')}\r\n`;
}

/** "PAY_HISTORY.xlsx" → "PAY_HISTORY-errors.csv". */
export const errorReportFileName = (fileName: string) =>
  `${fileName.replace(/\.[^.]+$/, '')}-errors.csv`;

/**
 * Upload rejected (pc-dbb.11): a rejected upload's error report in a wide modal (L10):
 * the file's details, the summary line and each problem row, downloadable as CSV.
 */
export function UploadErrorReportModal({
  target,
  onClose,
}: {
  target: UploadRef;
  onClose: () => void;
}) {
  const upload = useUpload(target.id);
  const data = upload.data;
  const report = data?.errorReport ?? null;
  const details: DescriptionListItem[] = data
    ? [
        { term: 'File name', description: <Text mono>{data.fileName}</Text> },
        { term: 'Uploaded', description: formatDate(data.uploadedAt, 'uploaded') },
        { term: 'Uploaded by', description: data.uploadedBy.name },
      ]
    : [];

  let body;
  if (upload.isError) {
    body = (
      <Alert
        tone="danger"
        title="The error report couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void upload.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (!data) {
    body = <SkeletonText lines={4} />;
  } else if (!report) {
    body = <Text as="p">This upload has no error report: it wasn’t rejected.</Text>;
  } else {
    body = (
      <Stack gap={5}>
        <DescriptionList items={details} />
        <Alert tone="danger" title={report.summaryLine}>
          No records were imported. Correct the problems below and upload the whole file again:
          partial re-uploads aren’t supported.
        </Alert>
        {/* Scroll, not cards: inside the modal it's under 768px wide even on a desktop. */}
        <Table aria-label="Problems" layout="fixed">
          <TableHead>
            <TableCell width="7%" numeric>
              Row
            </TableCell>
            <TableCell width="18%">Payee</TableCell>
            <TableCell width="15%">Field</TableCell>
            <TableCell width="18%">Value</TableCell>
            <TableCell width="42%">Problem</TableCell>
          </TableHead>
          <TableBody>
            {report.problemRows.map((p, index) => (
              <TableRow key={`${p.row}-${p.field}-${index}`}>
                <TableCell label="Row" numeric tone="muted">
                  {p.row}
                </TableCell>
                <TableCell rowHeader tone="strong">
                  {p.payee}
                </TableCell>
                <TableCell label="Field" tone="muted">
                  {p.field}
                </TableCell>
                <TableCell label="Value" tone="mono">
                  {p.value}
                </TableCell>
                <TableCell label="Problem">{p.problem}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {report.omittedProblemRows > 0 && (
          <Text tone="muted" size="meta">
            {`${report.omittedProblemRows.toLocaleString('en-US')} more ${
              report.omittedProblemRows === 1 ? 'problem isn’t' : 'problems aren’t'
            } shown. Fix these and upload the file again to see them.`}
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Upload rejected"
      footer={
        <>
          <Button
            variant="secondary"
            leadingIcon="download"
            onClick={() =>
              data && downloadText(errorReportCsv(data), errorReportFileName(data.fileName))
            }
            disabled={!report}
          >
            Download error report
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}

export const DELETE_PHRASE = 'DELETE';

/**
 * Delete upload (pc-dbb.12): what deleting the upload removes (the API's dry run), then
 * "DELETE" typed to confirm. Mount it for one upload, so the text starts empty.
 */
export function DeleteUploadModal({ target, onClose }: { target: UploadRef; onClose: () => void }) {
  const { toast } = useToast();
  const preview = useDeletionPreview(target.id);
  const remove = useDeleteUpload();
  const [matched, setMatched] = useState(false);
  const data = preview.data;
  const history = data?.kind !== 'pending';

  const confirm = () =>
    remove.mutate(target.id, {
      onSuccess: () => {
        toast({ tone: 'success', title: `${target.fileName} was deleted` });
        onClose();
      },
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: `${target.fileName} couldn’t be deleted`,
              description: 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });

  const summary: DescriptionListItem[] = data
    ? [
        { term: 'Records to delete', description: data.records.toLocaleString('en-US') },
        { term: 'Total amount', description: <Money value={dollars(data.totalCents)} /> },
        ...(history
          ? [
              {
                term: 'Pay dates affected',
                description: data.payDates.length
                  ? data.payDates.map((date) => formatDate(date)).join(', ')
                  : '—',
              },
            ]
          : []),
        { term: 'Reps affected', description: data.repsAffected.toLocaleString('en-US') },
      ]
    : [];

  let body;
  if (preview.isError) {
    body = (
      <Alert
        tone="danger"
        title="What this deletes couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void preview.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (!data) {
    body = <SkeletonText lines={4} />;
  } else {
    body = (
      <Stack gap={5}>
        <Text as="p">
          This permanently deletes every record created by <Text mono>{data.fileName}</Text>.{' '}
          {history
            ? 'Other uploads for the same pay dates aren’t affected.'
            : 'Other uploads for this pending date aren’t affected.'}
        </Text>
        <DescriptionList items={summary} />
        <TypeToConfirm
          phrase={DELETE_PHRASE}
          disabled={remove.isPending}
          onMatchChange={setMatched}
        />
      </Stack>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Delete upload"
      dismissable={!remove.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!matched || !data}
            loading={remove.isPending}
            loadingText="Deleting…"
            onClick={confirm}
          >
            Delete upload
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}

export const CLEAR_PHRASE = 'CLEAR';

/**
 * Clear pending pay (pc-dbb.15): what clearing the pending pay date removes, then
 * "CLEAR" typed to confirm. Mount it each time it opens, so the text starts empty.
 */
export function ClearPendingModal({
  summary,
  onClose,
}: {
  summary: PendingPaySummary;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const clear = useClearPendingPay();
  const [matched, setMatched] = useState(false);
  const date = summary.payDate ? formatDate(summary.payDate) : '—';

  const confirm = () =>
    clear.mutate(undefined, {
      onSuccess: () => {
        toast({ tone: 'success', title: `Pending pay for ${date} was cleared` });
        onClose();
      },
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: 'Pending pay couldn’t be cleared',
              description: 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });

  const items: DescriptionListItem[] = [
    { term: 'Pay date', description: date },
    { term: 'Total records', description: summary.records.toLocaleString('en-US') },
    { term: 'Total amount', description: <Money value={dollars(summary.totalCents)} /> },
    {
      term: 'Reps whose Pending page will empty',
      description: summary.reps.toLocaleString('en-US'),
    },
    { term: 'Uploads discarded', description: summary.uploads.toLocaleString('en-US') },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Clear pending pay"
      dismissable={!clear.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={clear.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!matched}
            loading={clear.isPending}
            loadingText="Clearing…"
            onClick={confirm}
          >
            Clear pending pay
          </Button>
        </>
      }
    >
      <Stack gap={5}>
        <Text as="p">
          This wipes the entire pending pay set for <strong>{date}</strong> and removes it from
          every rep’s Pending page.
        </Text>
        <DescriptionList items={items} />
        <TypeToConfirm
          phrase={CLEAR_PHRASE}
          hint="This is normally done once this pay date has been finalized and uploaded as pay history."
          disabled={clear.isPending}
          onMatchChange={setMatched}
        />
      </Stack>
    </Modal>
  );
}
